import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../lib/apiFetch';
import AppNavActions from '../components/AppNavActions';
import ThumbnailPreview from '../components/ThumbnailPreview';
import {
  FileText,
  AlertTriangle,
  Loader2,
  Settings,
  ShieldAlert,
  ChevronRight,
  Database,
  FolderTree,
  Info,
  Clock,
  ShieldCheck,
  Download,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  History,
  Layers,
  ExternalLink,
  User,
  Calendar,
  ScrollText,
  HatGlasses,
  Pencil,
  Save,
  Lock,
  LockOpen,
  Share2,
  Trash2,
  RotateCcw,
  XCircle,
  FileUp,
} from 'lucide-react';
import Tooltip from '../components/Tooltip';
import UploadNewVersionModal from '../components/UploadNewVersionModal';
import AddToGroupModal from '../components/AddToGroupModal';
import Toast from '../components/Toast';
import ToggleSwitch from '../components/ToggleSwitch';
import ConfirmPopUp from '../components/ConfirmPopUp';
import NotFoundMessage from '../components/NotFoundMessage';
import AccessDeniedMessage from '../components/AccessDeniedMessage';
import { RecordClassificationPicker, FunctionClassificationPicker } from '../components/ClassificationPicker';
import {
  GeographicalContextEditable,
  GeographicalContextAddressesReadOnly,
  GeographicalContextAddressesProcessing,
  GeographicalContextValuationsParcels,
} from '../components/GeographicalContextPanel';
import type { StoredGeographicalContext } from '../types/geographicalContext';
import RedactionModal from '../components/redaction/RedactionModal';
import RedactionsList from '../components/redaction/RedactionsList';
import { fetchRedactions } from '../lib/redactionApi';
import { useApiRoot } from '../contexts/ApiRootContext';
import type { RedactionManifest } from '../types/redaction';
import { getStoredGeographicalContextDisplayAddress } from '../types/geographicalContext';
import { formatBytes, getFileTypeLabel, getFileIcon, formatRetentionPeriod } from '../utils/fileUtils';
import { addedToGroupMessage } from '../utils/groupMembership';
import { formatGroupLabel } from '../utils/groupLabel';
import {
  getIscRowBackground,
  formatStatusLabel,
  formatExternalReferenceLabel,
  formatIscLevelName,
  getIscDescription,
  PSR_CLASSIFICATION_URL,
  PII_SENSITIVITY_OPTIONS,
} from '../constants/record';
import { SHOW_RETENTION_AND_DISPOSAL } from '../constants/feature_flags';
import { IscBadge, PiiBadge, MaoriDataBadge, StatusBadge } from '../components/badges';
import { useRecordEdit } from '../hooks/useRecordEdit';

// --- Interfaces ---

interface RecordFile {
  name: string;
  size: number;
  content_type: string;
  checksum?: string;
  checksum_algorithm?: string;
}

interface RecordMetadata {
  created?: string;
  creator?: string;
  modified?: string;
  modifier?: string;
  deleted?: string;
  deleter?: string;
  publisher?: string;
  source?: string;
  source_identifier?: string;
  relation?: string;
}

interface PersonalInformation {
  detected?: boolean;
  sensitivity_level?: string;
  entity_recognition?: Array<{
    entity: string;
    sensitivity_level: string;
    category: string;
    context_snippet: string;
  }>;
}

interface MaoriDataSovereignty {
  detected?: boolean;
  category?: string;
}

interface RecordClassification {
  level_1?: { code?: string; description?: string; name?: string };
  level_2?: { code?: string; name?: string };
  level_3?: { code?: string; description?: string; name?: string };
  disposal_recommendation?: string;
  minimum_retention_period?: string;
  retention_period_starts_at?: string | null;
  retention_period_ends_at?: string | null;
  retention_period_start_condition?: string;
}

interface FunctionClassification {
  domain?: { domain_name?: string; domain_key?: string; domain_description?: string };
  subdomain?: { subdomain_name?: string; subdomain_key?: string };
  function?: { function_name?: string; function_key?: string };
}

interface InformationSecurityClassification {
  level?: string;
}

interface Immutability {
  final?: boolean;
}

interface RecordDocument {
  id: string;
  title: string;
  file: RecordFile;
  status?: string;
  discoverable?: boolean;
  version: number;
  ingestion_process_id?: string;
  content_summary?: string;
  personal_information?: PersonalInformation;
  maori_data_sovereignty?: MaoriDataSovereignty;
  record_classification?: RecordClassification;
  function_classification?: FunctionClassification;
  information_security_classification?: InformationSecurityClassification;
  immutability?: Immutability;
  geographical_context?: StoredGeographicalContext;
  external_references?: { [key: string]: string };
  metadata?: RecordMetadata;
}

interface VersionHistoryEntry {
  id: string;
  version?: number;
  file?: RecordFile;
  status?: string;
  content_summary?: string;
  metadata?: RecordMetadata;
}

interface GroupMember {
  id: string;
  title?: string | null;
  content_type?: string | null;
}

interface RecordGroup {
  id: string;
  type?: string | null;
  title?: string | null;
  records: GroupMember[];
}

interface IngestionProcessDetails {
  id: string;
  ai_processing_mode?: string;
  ai_processing_previous_version_mode?: string;
  auto_process?: boolean;
  notify_user_on_completion?: boolean;
  completed_at?: string;
  confirmed_at?: string;
  confirmed_by?: string;
  initiated_at?: string;
  initiated_by?: string;
  uploaded_at?: string;
  custom_headers?: { [key: string]: string };
}

interface AuditLogEntry {
  id: string;
  timestamp?: string;
  user_id?: string;
  action?: string;
  ingestion_process_id?: string;
  record_id?: string;
  record_version_id?: string;
  details?: { [key: string]: any };
  correlation_id?: string;
}

interface HalLinks {
  [key: string]: { href: string };
}

// --- Constants ---

const BRAND_BG = "bg-emerald-800";
const BRAND_TEXT = "text-emerald-800";

const ISC_OPTIONS = [
  { value: 'unclassified', label: 'Unclassified' },
  { value: 'in-confidence', label: 'In-Confidence' },
  { value: 'sensitive', label: 'Sensitive' },
  { value: 'restricted', label: 'Restricted' },
];

// --- Helpers ---

const formatDateTime = (value?: string | null): string => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};



const formatAuditAction = (action?: string): string => {
  if (!action) return '';
  return action.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};


const MetadataRow = ({ label, value, icon, tooltip, onClick }: { label: string; value?: string | null; icon?: React.ReactNode; tooltip?: string; onClick?: () => void }) => (
  <div className="flex justify-between items-center text-sm py-2">
    <span className="text-gray-500 flex items-center gap-1.5">
      {icon}
      {label}
      {tooltip && (
        <Tooltip content={tooltip} position="bottom">
          <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
        </Tooltip>
      )}
    </span>
    {onClick && value ? (
      <button
        type="button"
        onClick={onClick}
        className="font-medium text-emerald-700 hover:underline text-right max-w-[60%] truncate"
        title={value}
      >
        {value}
      </button>
    ) : (
      <span className="font-medium text-gray-900 text-right max-w-[60%] truncate" title={value || '—'}>
        {value || '—'}
      </span>
    )}
  </div>
);

// --- Lifecycle Timeline ---

const parseISODurationToMonths = (iso?: string | null): number | null => {
  if (!iso) return null;
  const match = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?$/.exec(iso);
  if (!match) return null;
  const total = parseInt(match[1] || '0') * 12 + parseInt(match[2] || '0') + Math.round(parseInt(match[3] || '0') / 30);
  return total > 0 ? total : null;
};

const addMonthsToDate = (date: Date, months: number): Date => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

const formatShortDate = (d: Date): string =>
  d.toLocaleDateString('en-NZ', { month: 'short', year: '2-digit' });

interface LifecyclePhase {
  name: string;
  startDate: Date;
  endDate: Date;
  durationMonths: number;
  barColor: string;
  dotColor: string;
  isCurrent: boolean;
  isPast: boolean;
}

const LIFECYCLE_PHASE_ORDER: Record<string, number> = {
  draft: 0,
  pending_finalisation: 1,
  final: 2,
  pending_disposal: 3,
  archived: 4,
  destroyed: 4,
};

const LifecycleTimeline = ({ record }: { record: RecordDocument }) => {
  const createdDate = record.metadata?.created ? new Date(record.metadata.created) : null;
  if (!createdDate || isNaN(createdDate.getTime())) return null;

  const retentionMonths = parseISODurationToMonths(record.record_classification?.minimum_retention_period);
  if (retentionMonths === null) return null;

  const isImmutable = record.immutability?.final === true;
  const isDestruction = record.record_classification?.disposal_recommendation?.toLowerCase() === 'destruction';
  const currentStatus = record.status?.toLowerCase() || '';
  const currentPhaseIdx = LIFECYCLE_PHASE_ORDER[currentStatus] ?? -1;

  const phases: LifecyclePhase[] = [];
  let cursor = new Date(createdDate);

  const pushPhase = (
    name: string, durationMonths: number, phaseIdx: number,
    barColor: string, dotColor: string,
  ) => {
    const end = addMonthsToDate(cursor, durationMonths);
    phases.push({
      name, startDate: new Date(cursor), endDate: end, durationMonths,
      barColor, dotColor,
      isCurrent: currentPhaseIdx === phaseIdx,
      isPast: currentPhaseIdx > phaseIdx,
    });
    cursor = end;
  };

  if (!isImmutable) {
    pushPhase('Draft', 3, 0, 'bg-sky-300/80', 'bg-sky-400');
    pushPhase('Pending Finalisation', 1, 1, 'bg-sky-400/80', 'bg-sky-500');
  }

  pushPhase('Final', retentionMonths, 2, 'bg-stone-300/70', 'bg-stone-400');
  pushPhase('Disposition Schedule', 3, 3, 'bg-amber-300/70', 'bg-amber-400');

  if (!isDestruction) {
    pushPhase('Archived', 6, 4, 'bg-slate-300/60', 'bg-slate-400');
  }

  const totalMonths = phases.reduce((sum, p) => sum + p.durationMonths, 0);
  const destructionDate = isDestruction ? new Date(cursor) : null;
  const isTerminal = (i: number) => i === phases.length - 1;

  const activeMonths = isDestruction ? totalMonths : totalMonths - 6;
  const totalSpanLabel = isDestruction
    ? `${(activeMonths / 12).toFixed(1)} Years`
    : 'Permanent';

  const now = new Date();
  const timelineStartMs = phases[0].startDate.getTime();
  const timelineEndMs = phases[phases.length - 1].endDate.getTime();
  const totalTimeMs = timelineEndMs - timelineStartMs;
  const nowProgress = totalTimeMs > 0 ? (now.getTime() - timelineStartMs) / totalTimeMs : 0;
  const showNowMarker = nowProgress > 0.005 && nowProgress < 0.995;
  const nowPercent = Math.max(0, Math.min(100, nowProgress * 100));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-4 bg-gray-50/50 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 rounded-t-xl">
        <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
          <Clock className="w-4 h-4 text-emerald-600" />
          Lifecycle Timeline
        </h3>
        <div className="flex items-center gap-5">
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Total Span</div>
            <div className="text-sm font-bold text-gray-900">{totalSpanLabel}</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Phases</div>
            <div className="text-sm font-bold text-gray-900">{phases.length}</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Status</div>
            <div className="text-sm font-bold text-emerald-700">{formatStatusLabel(record.status)}</div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Phase labels */}
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {phases.map((phase, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${phase.dotColor} ${phase.isCurrent ? 'ring-2 ring-emerald-400/50' : ''}`} />
              <div>
                <div className={`text-xs font-semibold ${phase.isCurrent ? 'text-gray-900' : phase.isPast ? 'text-gray-400' : 'text-gray-500'}`}>
                  {phase.name}
                </div>
                <div className="text-[10px] text-gray-400">
                  {isTerminal(i) && !isDestruction
                    ? `From ${formatShortDate(phase.startDate)}`
                    : `${formatShortDate(phase.startDate)} – ${formatShortDate(phase.endDate)}`}
                </div>
              </div>
            </div>
          ))}
          {isDestruction && destructionDate && (
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full bg-red-500 ${currentStatus === 'destroyed' ? 'ring-2 ring-red-300' : ''}`} />
              <div>
                <div className={`text-xs font-semibold ${currentStatus === 'destroyed' ? 'text-red-700' : 'text-gray-500'}`}>
                  Destroyed
                </div>
                <div className="text-[10px] text-gray-400">
                  {formatShortDate(destructionDate)}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Timeline bar */}
        <div>
          <div className="flex items-center gap-0">
            <div className="relative h-8 rounded-lg overflow-hidden flex bg-gray-100 flex-1">
              {phases.map((phase, i) => (
                <div
                  key={i}
                  className={`h-full ${phase.barColor} relative flex items-center justify-center overflow-hidden border-r border-white/60 last:border-r-0 ${phase.isCurrent ? 'ring-1 ring-inset ring-emerald-500/40' : ''}`}
                  style={{ flex: phase.durationMonths }}
                  title={`${phase.name}: ${formatShortDate(phase.startDate)} – ${isTerminal(i) && !isDestruction ? '∞' : formatShortDate(phase.endDate)}`}
                >
                  {phase.durationMonths >= 12 && (phase.durationMonths / totalMonths) > 0.15 && (
                    <span className="text-[10px] font-bold text-gray-500/60 whitespace-nowrap select-none">
                      {(phase.durationMonths / 12).toFixed(1)} Years
                    </span>
                  )}
                </div>
              ))}
              {showNowMarker && (
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-gray-900 z-10 pointer-events-none"
                  style={{ left: `${nowPercent}%` }}
                >
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[4px] border-r-[4px] border-b-[5px] border-l-transparent border-r-transparent border-b-gray-900" />
                </div>
              )}
            </div>
            {isDestruction && (
              <div className="flex flex-col items-center justify-center ml-1.5 shrink-0" title={`Destroyed: ${destructionDate ? formatShortDate(destructionDate) : ''}`}>
                <div className="w-3 h-8 flex items-center justify-center">
                  <div className="w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-red-200" />
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-between items-center mt-1.5">
            <span className="text-[10px] text-gray-400">{formatShortDate(phases[0].startDate)}</span>
            <span className="text-[10px] text-gray-400 italic">
              Estimated · {formatRetentionPeriod(record.record_classification?.minimum_retention_period)} retention
            </span>
            <span className="text-[10px] text-gray-400">
              {isDestruction && destructionDate ? formatShortDate(destructionDate) : '∞'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---

export default function RecordDetails() {
  const navigate = useNavigate();
  const { getHref } = useApiRoot();
  const redactionCategoriesHref = getHref('redaction_categories');
  const { id, versionId } = useParams<{ id: string; versionId?: string }>();

  const [recordLoading, setRecordLoading] = useState(true);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [recordAccessDenied, setRecordAccessDenied] = useState(false);
  const [record, setRecord] = useState<RecordDocument | null>(null);

  const [versionHistory, setVersionHistory] = useState<VersionHistoryEntry[]>([]);
  const [versionHistoryLoading, setVersionHistoryLoading] = useState(false);

  const [redactions, setRedactions] = useState<RedactionManifest[]>([]);
  const [redactionsLoading, setRedactionsLoading] = useState(false);
  const [redactionModalOpen, setRedactionModalOpen] = useState(false);
  const [redactionsExpanded, setRedactionsExpanded] = useState(false);

  const [ingestionDetails, setIngestionDetails] = useState<IngestionProcessDetails | null>(null);
  const [ingestionLoading, setIngestionLoading] = useState(false);

  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);

  const [groups, setGroups] = useState<RecordGroup[]>([]);

  const [isDownloading, setIsDownloading] = useState(false);
  const [recordLinks, setRecordLinks] = useState<HalLinks>({});

  // Collapsible sections
  const [ingestionExpanded, setIngestionExpanded] = useState(false);
  const [auditExpanded, setAuditExpanded] = useState(false);
  const [versionExpanded, setVersionExpanded] = useState(true);
  const [groupExpanded, setGroupExpanded] = useState(true);
  const [piiEntitiesExpanded, setPiiEntitiesExpanded] = useState(false);
  const [advancedExpanded, setAdvancedExpanded] = useState(false);

  const [finaliseConfirmOpen, setFinaliseConfirmOpen] = useState(false);
  const [unfinaliseConfirmOpen, setUnfinaliseConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteSuccessVisible, setDeleteSuccessVisible] = useState(false);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [newVersionModalOpen, setNewVersionModalOpen] = useState(false);
  const [addToGroupModalOpen, setAddToGroupModalOpen] = useState(false);

  const downloadHalHref = recordLinks.download?.href ?? null;
  const thumbnailHalHref = recordLinks.thumbnail?.href ?? null;
  const editHalHref = recordLinks.edit?.href ?? null;
  const deleteHalHref = recordLinks.delete?.href ?? null;
  const restoreHalHref = recordLinks.restore?.href ?? null;
  const finaliseHalHref = recordLinks.finalise?.href ?? null;
  const unfinaliseHalHref = recordLinks.unfinalise?.href ?? null;
  const newVersionHalHref = recordLinks.new_version?.href ?? null;
  const addToGroupHalHref = recordLinks.add_to_group?.href ?? null;
  const versionHistoryHalHref = recordLinks.version_history?.href ?? null;
  const redactionsHalHref = recordLinks.redactions?.href ?? null;
  const createRedactionHalHref = recordLinks.create_redaction?.href ?? null;
  const redactionCandidatesHalHref = recordLinks.redaction_candidates?.href ?? null;
  const auditLogsHalHref = recordLinks.audit_logs?.href ?? null;
  const groupsHalHref = recordLinks.groups?.href ?? null;
  const ingestionProcessHalHref = recordLinks.ingestion_process?.href ?? null;

  // Apply a HAL record response: store the record, then fetch every secondary
  // resource advertised by _links.
  const applyRecordResponse = useCallback((
    data: { _links?: HalLinks; _embedded?: { record?: RecordDocument | null } },
  ) => {
    const links = (data._links || {}) as HalLinks;

    setRecord(data._embedded?.record ?? null);
    setRecordLinks(links);

    if (links.version_history?.href) {
      setVersionHistoryLoading(true);
      apiFetch(links.version_history.href)
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(payload => setVersionHistory(payload._embedded?.version_history || []))
        .catch(() => setVersionHistory([]))
        .finally(() => setVersionHistoryLoading(false));
    } else {
      setVersionHistory([]);
      setVersionHistoryLoading(false);
    }

    if (links.redactions?.href) {
      setRedactionsLoading(true);
      fetchRedactions(links.redactions.href)
        .then(setRedactions)
        .catch(() => setRedactions([]))
        .finally(() => setRedactionsLoading(false));
    } else {
      setRedactions([]);
      setRedactionsLoading(false);
    }

    if (links.audit_logs?.href) {
      setAuditLogsLoading(true);
      apiFetch(links.audit_logs.href)
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(payload => setAuditLogs(payload._embedded?.audit_logs || []))
        .catch(() => setAuditLogs([]))
        .finally(() => setAuditLogsLoading(false));
    } else {
      setAuditLogs([]);
      setAuditLogsLoading(false);
    }

    if (links.groups?.href) {
      apiFetch(links.groups.href)
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(payload => setGroups(payload._embedded?.groups || []))
        .catch(() => setGroups([]));
    } else {
      setGroups([]);
    }

    if (links.ingestion_process?.href) {
      setIngestionLoading(true);
      apiFetch(links.ingestion_process.href)
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(payload => setIngestionDetails(payload._embedded?.ingestion_process || null))
        .catch(() => setIngestionDetails(null))
        .finally(() => setIngestionLoading(false));
    } else {
      setIngestionDetails(null);
      setIngestionLoading(false);
    }
  }, []);

  const edit = useRecordEdit(id, record, applyRecordResponse);

  // GET the primary record resource, then apply its HAL response. Pass
  // { background: true } to refresh without the full-page loading state.
  const loadRecord = useCallback(async (opts?: { background?: boolean }) => {
    if (!id) return;
    const background = opts?.background ?? false;

    if (!background) {
      setRecordLoading(true);
      setRecordError(null);
      setRecordAccessDenied(false);
      setRecordLinks({});
    }

    try {
      const url = versionId
        ? `/api/v1/records/${id}/versions/${versionId}`
        : `/api/v1/records/${id}`;
      const response = await apiFetch(url);
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (!background && response.status === 403) {
          setRecordAccessDenied(true);
          setRecordError(errData.error || 'Access denied');
          return;
        }
        throw new Error(errData.error || `Failed to load record (${response.status})`);
      }
      applyRecordResponse(await response.json());
    } catch (err: any) {
      if (!background) setRecordError(err.message || 'Failed to load record');
    } finally {
      if (!background) setRecordLoading(false);
    }
  }, [id, versionId, applyRecordResponse]);

  const reloadRecord = useCallback(() => loadRecord({ background: true }), [loadRecord]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  useEffect(() => {
    if (record?.title) {
      document.title = `ARA - ${record.title}`;
    } else if (record) {
      document.title = 'ARA - Untitled record';
    }
    return () => {
      document.title = 'ARA';
    };
  }, [record]);

  const handleDownload = async () => {
    if (!downloadHalHref || isDownloading) return;
    setIsDownloading(true);
    try {
      const response = await apiFetch(downloadHalHref);
      if (!response.ok) {
        throw new Error('Failed to generate download link');
      }
      const data = await response.json();
      if (data.downloadUrl) {
        window.open(data.downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err: any) {
      console.error('Download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  // --- Loading State ---
  if (recordLoading) {
    return (
      <div className="flex-1 bg-white text-gray-900 font-sans">
        <Navbar navigate={navigate} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            <p className="text-gray-500 text-sm">Loading record details...</p>
          </div>
        </main>
      </div>
    );
  }

  // --- Error State ---
  if (recordError || !record) {
    return (
      <div className="flex-1 bg-white text-gray-900 font-sans">
        <Navbar navigate={navigate} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {recordAccessDenied ? (
            <AccessDeniedMessage
              message="You don't have permission to view this record."
              onGoBack={() => navigate(-1)}
            />
          ) : (
            <NotFoundMessage
              message={recordError || 'The requested record could not be loaded.'}
              onGoBack={() => navigate(-1)}
            />
          )}
        </main>
      </div>
    );
  }

  const canRedactRecord = Boolean(
    createRedactionHalHref && redactionCandidatesHalHref && redactionCategoriesHref,
  );
  const redactionUnavailableReason = 'Redaction is not available for this record.';

  const currentVersion = record.version;
  const latestVersionInHistory =
    versionHistory.length > 0
      ? Math.max(...versionHistory.map((v) => v.version ?? 0))
      : currentVersion;

  return (
    <div className="flex-1 bg-white text-gray-900 font-sans selection:bg-emerald-100 selection:text-emerald-900">
      <Navbar navigate={navigate} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-8 duration-500 pb-20">

          {/* Back navigation */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-sm text-gray-500 hover:text-emerald-700 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back
          </button>

          {/* Error banner */}
          {edit.saveError && (
            <div className="bg-red-50 rounded-2xl shadow-sm border border-red-200 p-6 mb-6 animate-in fade-in slide-in-from-top-4 duration-500 flex items-start space-x-4">
              <div className="p-3 bg-red-100 rounded-full shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-red-800 tracking-tight">Error</h3>
                <p className="text-sm text-red-600 mt-1 max-w-2xl">{edit.saveError}</p>
              </div>
              <button onClick={edit.clearSaveError} className="text-red-400 hover:text-red-600 transition-colors shrink-0">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
          )}

          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-visible relative">

            {/* Dark Header */}
            <div className="bg-gray-900 text-white p-6 flex flex-col md:flex-row md:items-start justify-between gap-6 rounded-t-3xl">
              <div className="flex items-start space-x-4 flex-1">
                <div className="p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 shrink-0">
                  {getFileIcon(record.file.content_type)}
                </div>
                <div className="flex-1 w-full min-w-0">
                  {edit.isEditing ? (
                    <input
                      type="text"
                      value={edit.getFieldValue('title') ?? record.title}
                      onChange={(e) => edit.setEditField('title', e.target.value)}
                      className="text-xl font-bold tracking-tight break-words pr-4 w-full bg-white/10 border border-white/30 rounded-lg px-3 py-1.5 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    />
                  ) : (
                    <h1 className="text-xl font-bold tracking-tight break-words pr-4">{record.title}</h1>
                  )}

                  <div className="flex flex-wrap items-center mt-2 gap-3 text-black">
                    <StatusBadge status={record.status} immutabilityFinal={record.immutability?.final} />
                    <IscBadge level={record.information_security_classification?.level} />
                    <PiiBadge detected={record.personal_information?.detected} level={record.personal_information?.sensitivity_level} />
                    <MaoriDataBadge detected={record.maori_data_sovereignty?.detected} />
                  </div>
                </div>
              </div>

            </div>

            {/* Edit mode banner */}
            {edit.isEditing && (
              <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-amber-800">
                  <Pencil className="w-4 h-4" />
                  <span className="text-sm font-semibold">Editing record</span>
                </div>
                <div className="flex items-center space-x-3 shrink-0">
                  <button
                    onClick={edit.handleCancelEdit}
                    disabled={edit.isSaving}
                    className="px-5 py-2 rounded-xl text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Discard Changes
                  </button>
                  <button
                    onClick={edit.handleSave}
                    disabled={edit.isSaving || Object.keys(edit.editedFields).length === 0 || edit.addressIsStale}
                    className={`px-6 py-2 rounded-xl text-sm font-bold text-white shadow-lg shadow-emerald-200 flex items-center transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none ${BRAND_BG}`}
                  >
                    {edit.isSaving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {edit.isSaving ? 'Saving...' : 'Save Record'}
                  </button>
                </div>
              </div>
            )}

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[600px]">

              {/* Left Column (4/12) */}
              <div className="lg:col-span-4 bg-gray-50 border-r border-gray-100 p-6 space-y-6 flex flex-col">

                <ThumbnailPreview thumbnailHref={thumbnailHalHref} />

                {/* Download Button — only when HAL download link is present */}
                {!edit.isEditing && downloadHalHref && (
                  <button
                    onClick={handleDownload}
                    disabled={isDownloading || !downloadHalHref}
                    className={`w-full py-3 ${BRAND_BG} text-white text-sm font-bold rounded-xl hover:bg-emerald-700 transition-all flex items-center justify-center shadow-lg shadow-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isDownloading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    {isDownloading ? 'Generating link...' : 'Download File'}
                  </button>
                )}

                {/* Actions dropdown */}
                {!edit.isEditing && (
                  <div className="relative" ref={edit.actionsMenuRef}>
                    <button
                      onClick={() => edit.setActionsMenuOpen(!edit.actionsMenuOpen)}
                      className="w-full py-3 border border-gray-200 bg-white text-gray-700 text-sm font-bold rounded-xl hover:bg-gray-50 transition-all flex items-center justify-center shadow-sm"
                      aria-haspopup="true"
                      aria-expanded={edit.actionsMenuOpen}
                    >
                      Actions
                      <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${edit.actionsMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {edit.actionsMenuOpen && (
                      <div className="absolute left-0 right-0 bottom-full z-50 pb-1">
                        <div role="menu" className="rounded-xl border border-gray-200 bg-white py-1 shadow-lg ring-1 ring-black/5">
                          <Tooltip content="Copy a direct link to this record." position="right" className="w-full">
                            <button
                              type="button"
                              role="menuitem"
                              className="w-full px-3 py-2.5 text-left text-sm font-medium text-gray-700 transition hover:bg-emerald-50 hover:text-emerald-800 flex items-center gap-2"
                              onClick={() => {
                                navigator.clipboard.writeText(window.location.href);
                                edit.setActionsMenuOpen(false);
                                edit.showToast('Link copied to clipboard');
                              }}
                            >
                              <Share2 className="w-4 h-4" />
                              Copy Link
                            </button>
                          </Tooltip>
                          <Tooltip
                            content={
                              canRedactRecord
                                ? 'Create a copy for release with content permanently removed.'
                                : redactionUnavailableReason
                            }
                            position="right"
                            className="w-full"
                          >
                            <button
                              type="button"
                              role="menuitem"
                              disabled={!canRedactRecord}
                              onClick={() => {
                                edit.setActionsMenuOpen(false);
                                setRedactionModalOpen(true);
                              }}
                              className={`w-full px-3 py-2.5 text-left text-sm font-medium flex items-center gap-2 ${
                                canRedactRecord
                                  ? 'text-gray-700 transition hover:bg-emerald-50 hover:text-emerald-800'
                                  : 'text-gray-300 cursor-not-allowed'
                              }`}
                            >
                              <HatGlasses className="w-4 h-4" />
                              Redact & Download
                            </button>
                          </Tooltip>
                          <div className="my-1 border-t border-gray-100" />
                          {editHalHref && (
                            <button
                              type="button"
                              role="menuitem"
                              className="w-full px-3 py-2.5 text-left text-sm font-medium text-gray-700 transition hover:bg-emerald-50 hover:text-emerald-800 flex items-center gap-2"
                              onClick={edit.startEditing}
                            >
                              <Pencil className="w-4 h-4" />
                              Edit Data
                            </button>
                          )}
                          {newVersionHalHref && (
                            <Tooltip content="Upload a new version of this record." position="right" className="w-full">
                              <button
                                type="button"
                                role="menuitem"
                                className="w-full px-3 py-2.5 text-left text-sm font-medium text-gray-700 transition hover:bg-emerald-50 hover:text-emerald-800 flex items-center gap-2"
                                onClick={() => {
                                  edit.setActionsMenuOpen(false);
                                  setNewVersionModalOpen(true);
                                }}
                              >
                                <FileUp className="w-4 h-4" />
                                Upload New Version
                              </button>
                            </Tooltip>
                          )}
                          {finaliseHalHref && (
                            <Tooltip content="Mark this record as final and immutable." position="right" className="w-full">
                              <button
                                type="button"
                                role="menuitem"
                                disabled={edit.isFinalising}
                                className="w-full px-3 py-2.5 text-left text-sm font-medium text-gray-700 transition hover:bg-emerald-50 hover:text-emerald-800 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => {
                                  edit.setActionsMenuOpen(false);
                                  setFinaliseConfirmOpen(true);
                                }}
                              >
                                {edit.isFinalising ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                                Mark as Final
                              </button>
                            </Tooltip>
                          )}
                          {unfinaliseHalHref && (
                            <Tooltip content="Revert this record to an editable draft." position="right" className="w-full">
                              <button
                                type="button"
                                role="menuitem"
                                disabled={edit.isUnfinalising}
                                className="w-full px-3 py-2.5 text-left text-sm font-medium text-gray-700 transition hover:bg-emerald-50 hover:text-emerald-800 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => {
                                  edit.setActionsMenuOpen(false);
                                  setUnfinaliseConfirmOpen(true);
                                }}
                              >
                                {edit.isUnfinalising ? <Loader2 className="w-4 h-4 animate-spin" /> : <LockOpen className="w-4 h-4" />}
                                Unfinalise Record
                              </button>
                            </Tooltip>
                          )}
                          {addToGroupHalHref && (
                            <>
                              <div className="my-1 border-t border-gray-100" />
                              <Tooltip content="Add this record to a group." position="right" className="w-full">
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="w-full px-3 py-2.5 text-left text-sm font-medium text-gray-700 transition hover:bg-emerald-50 hover:text-emerald-800 flex items-center gap-2"
                                  onClick={() => {
                                    edit.setActionsMenuOpen(false);
                                    setAddToGroupModalOpen(true);
                                  }}
                                >
                                  <Layers className="w-4 h-4" />
                                  Add to Group
                                </button>
                              </Tooltip>
                            </>
                          )}
                          {deleteHalHref && (
                            <>
                              <div className="my-1 border-t border-gray-100" />
                              <Tooltip content="Delete this record." position="right" className="w-full">
                                <button
                                  type="button"
                                  role="menuitem"
                                  disabled={edit.isDeleting}
                                  className="w-full px-3 py-2.5 text-left text-sm font-medium text-red-600 transition hover:bg-red-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                  onClick={() => {
                                    edit.setActionsMenuOpen(false);
                                    setDeleteConfirmOpen(true);
                                  }}
                                >
                                  {edit.isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                  Delete Record
                                </button>
                              </Tooltip>
                            </>
                          )}
                          {restoreHalHref && (
                            <>
                              <Tooltip content="Restore this record to its previous status." position="right" className="w-full">
                                <button
                                  type="button"
                                  role="menuitem"
                                  disabled={edit.isRestoring}
                                  className="w-full px-3 py-2.5 text-left text-sm font-medium text-gray-700 transition hover:bg-emerald-50 hover:text-emerald-800 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                  onClick={() => {
                                    edit.setActionsMenuOpen(false);
                                    setRestoreConfirmOpen(true);
                                  }}
                                >
                                  {edit.isRestoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                                  Restore Record
                                </button>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <GeographicalContextEditable
                  tooltipContent="Identifies physical addresses and locations."
                  suggestedAddress={edit.addressInput || getStoredGeographicalContextDisplayAddress(record.geographical_context) || ''}
                  onAddressChange={edit.setAddressInput}
                  onValidate={edit.handleValidateAddress}
                  isValidating={edit.isValidatingAddress}
                  readOnly={!edit.isEditing}
                  showValidateButton={edit.addressIsStale}
                >
                  {'geographical_context' in edit.editedFields ? (
                    <>
                      <GeographicalContextAddressesProcessing
                        parsed_street={edit.editedFields.geographical_context?.parsed_street}
                        validated_street={edit.editedFields.geographical_context?.validated_street}
                        parsed_addresses={edit.editedFields.geographical_context?.parsed_addresses}
                        validated_addresses={edit.editedFields.geographical_context?.validated_addresses}
                      />
                      <GeographicalContextValuationsParcels
                        rating_valuations={edit.editedFields.geographical_context?.rating_valuations}
                        parcels={edit.editedFields.geographical_context?.parcels}
                      />
                    </>
                  ) : (
                    <>
                      <GeographicalContextAddressesReadOnly
                        street={record.geographical_context?.street}
                        addresses={record.geographical_context?.addresses}
                      />
                      <GeographicalContextValuationsParcels
                        rating_valuations={record.geographical_context?.rating_valuations}
                        parcels={record.geographical_context?.parcels}
                      />
                    </>
                  )}
                </GeographicalContextEditable>

                {/* File Details */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="p-4 bg-gray-50/50 border-b border-gray-100 rounded-t-xl">
                    <h3 className="text-sm font-bold text-gray-900">File Details</h3>
                  </div>
                  <div className="p-4 space-y-1 divide-y divide-gray-50">
                    <MetadataRow label="Type" value={getFileTypeLabel(record.file.content_type)} />
                    <div className="flex justify-between items-start gap-2 text-sm py-2">
                      <span className="text-gray-500 shrink-0">Filename</span>
                      <span
                        className="font-medium text-gray-900 text-right break-all max-w-[65%]"
                        title={record.file.name}
                      >
                        {record.file.name}
                      </span>
                    </div>
                    <MetadataRow label="Size" value={formatBytes(record.file.size)} />
                    <MetadataRow label="Version" value={`v${currentVersion}`} />
                    <div className="flex justify-between items-center text-sm py-2">
                      <span className="text-gray-500 shrink-0 mr-2 flex items-center">
                        File Integrity
                        <Tooltip content="Digital fingerprint ensuring the file cannot be altered."><Info className="w-3.5 h-3.5 text-gray-400 ml-1.5 cursor-help" /></Tooltip>
                      </span>
                      <div className="flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-600">
                        <ShieldCheck className="w-4 h-4 mr-1.5" />
                        <span className="font-semibold text-xs">Secured & Verified</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Metadata */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="p-4 bg-gray-50/50 border-b border-gray-100 rounded-t-xl">
                    <h3 className="text-sm font-bold text-gray-900">Metadata</h3>
                  </div>
                  <div className="p-4 space-y-1 divide-y divide-gray-50">
                    <MetadataRow label="Created" value={formatDateTime(record.metadata?.created)} icon={<Calendar className="w-3.5 h-3.5" />} />
                    <MetadataRow label="Created By" value={record.metadata?.creator} icon={<User className="w-3.5 h-3.5" />} />
                    <MetadataRow label="Modified" value={formatDateTime(record.metadata?.modified)} icon={<Calendar className="w-3.5 h-3.5" />} />
                    <MetadataRow label="Modified By" value={record.metadata?.modifier} icon={<User className="w-3.5 h-3.5" />} />
                    {record.metadata?.deleted && (
                      <>
                        <MetadataRow label="Deleted" value={formatDateTime(record.metadata.deleted)} icon={<Calendar className="w-3.5 h-3.5" />} />
                        <MetadataRow label="Deleted By" value={record.metadata?.deleter} icon={<User className="w-3.5 h-3.5" />} />
                      </>
                    )}
                  </div>
                </div>

                {/* Advanced (collapsible) */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                  <button
                    onClick={() => setAdvancedExpanded(!advancedExpanded)}
                    className="w-full p-4 bg-gray-50/50 border-b border-gray-100 rounded-t-xl flex justify-between items-center"
                  >
                    <h3 className="text-sm font-bold text-gray-900 flex items-center">
                      <Settings className="w-4 h-4 text-gray-400 mr-2" />
                      Advanced
                    </h3>
                    {advancedExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </button>

                  {advancedExpanded && (
                    <div className="p-4 space-y-1 divide-y divide-gray-50">
                      {record.external_references && Object.keys(record.external_references).length > 0 &&
                        Object.entries(record.external_references).map(([key, value]) => (
                          <MetadataRow key={key} label={formatExternalReferenceLabel(key)} value={value} tooltip="A reference to an object in another system." />
                        ))
                      }
                      {record.metadata?.source && (
                        <MetadataRow label={record.metadata.source} value={record.metadata?.source_identifier} tooltip="The related resource from which the described resource is derived." />
                      )}
                      {record.metadata?.publisher && (
                        <MetadataRow label="Publisher" value={record.metadata.publisher} tooltip="The entity responsible for making the resource available." />
                      )}
                      {record.metadata?.relation && (
                        <MetadataRow label="Correlation ID" value={record.metadata.relation} />
                      )}
                      {record.ingestion_process_id && (
                        <MetadataRow
                          label="Ingestion Process"
                          value={record.ingestion_process_id}
                        />
                      )}
                      <MetadataRow label="Record ID" value={record.id} />
                    </div>
                  )}
                </div>

              </div>

              {/* Right Column (8/12) */}
              <div className="lg:col-span-8 p-6 md:p-8 space-y-6">

                {/* Summary */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="p-4 bg-gray-50/50 border-b border-gray-100 flex items-center gap-2 rounded-t-xl">
                    <FileText className="w-4 h-4 text-emerald-600" />
                    <h3 className="text-sm font-bold text-gray-900 flex items-center">
                      Summary
                      <Tooltip content="AI-generated overview of the document's main points."><Info className="w-3.5 h-3.5 text-gray-400 ml-1.5 cursor-help" /></Tooltip>
                    </h3>
                  </div>
                  <div className="p-4">
                    {edit.isEditing ? (
                      <textarea
                        value={edit.getFieldValue('content_summary') ?? record.content_summary ?? ''}
                        onChange={(e) => edit.setEditField('content_summary', e.target.value)}
                        rows={6}
                        className="w-full text-sm text-gray-700 leading-relaxed bg-white p-4 rounded-lg border border-emerald-300 min-h-[100px] focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-y"
                      />
                    ) : (
                      <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap bg-gray-50 p-4 rounded-lg border border-gray-100 min-h-[100px]">
                        {record.content_summary || <span className="italic text-gray-400">No summary available.</span>}
                      </div>
                    )}
                  </div>
                </div>

                {/* Lifecycle Timeline */}
                {SHOW_RETENTION_AND_DISPOSAL && !edit.isEditing && <LifecycleTimeline record={record} />}

                {/* Record Classification */}
                {(() => {
                  const rc = edit.editedFields.record_classification ?? record.record_classification;
                  const hasClassification = rc?.level_1?.name || rc?.level_2?.name || rc?.level_3?.name;
                  return (
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                      <div className="p-4 bg-gray-50/50 border-b border-gray-100 rounded-t-xl flex justify-between items-center">
                        <label className="text-sm font-bold text-gray-900 flex items-center">
                          <FolderTree className="w-4 h-4 text-emerald-600 mr-2" />
                          Record Classification
                          <Tooltip content="The formal categorisation determining the type of record."><Info className="w-3.5 h-3.5 text-gray-400 ml-1.5 cursor-help" /></Tooltip>
                        </label>
                      </div>
                      <div className="p-4 space-y-3">
                        {hasClassification ? (
                          <div className="space-y-3">
                            <div>
                              <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400 mb-0.5">Function</div>
                              <div
                                className={`text-sm font-semibold text-gray-900 flex items-center ${edit.isEditing ? 'cursor-pointer hover:text-emerald-600 transition-colors' : ''}`}
                                onClick={edit.isEditing ? () => edit.setRecordClassificationPickerState({ open: true, level: 1, level1: null, level2: null }) : undefined}
                              >
                                {edit.isEditing && <ChevronRight className="w-3 h-3 mr-1 text-emerald-600 shrink-0" />}
                                {rc?.level_1?.name || <span className="italic text-gray-400">Not set</span>}
                              </div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400 mb-0.5">Activity</div>
                              <div
                                className={`text-sm font-semibold text-emerald-800 flex items-center ${edit.isEditing ? 'cursor-pointer hover:text-emerald-600 transition-colors' : ''}`}
                                onClick={edit.isEditing && rc?.level_1?.name ? () => edit.setRecordClassificationPickerState({ open: true, level: 2, level1: rc.level_1.name ?? null, level2: null }) : undefined}
                              >
                                {edit.isEditing && <ChevronRight className="w-3 h-3 mr-1 text-emerald-600 shrink-0" />}
                                {rc?.level_2?.name || <span className="italic text-gray-400">Not set</span>}
                              </div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400 mb-0.5">Record Type</div>
                              <div
                                className={`text-sm font-bold text-gray-900 break-words leading-tight flex items-center ${edit.isEditing ? 'cursor-pointer hover:text-emerald-600 transition-colors' : ''}`}
                                onClick={edit.isEditing && rc?.level_1?.name && rc?.level_2?.name ? () => edit.setRecordClassificationPickerState({ open: true, level: 3, level1: rc.level_1.name ?? null, level2: rc.level_2.name ?? null }) : undefined}
                              >
                                {edit.isEditing && <ChevronRight className="w-3 h-3 mr-1 text-emerald-600 shrink-0" />}
                                {rc?.level_3?.name || <span className="italic text-gray-400">Not set</span>}
                              </div>
                            </div>
                            {rc?.level_3?.description && (
                              <details className="group/details pb-1">
                                <summary className="text-xs text-emerald-600 font-semibold cursor-pointer select-none hover:text-emerald-700 list-none flex items-center">
                                  <ChevronRight className="w-3 h-3 mr-1 transition-transform group-open/details:rotate-90" />
                                  Show Description
                                </summary>
                                <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded border border-gray-100 italic">
                                  {rc.level_3.description}
                                </div>
                              </details>
                            )}
                          </div>
                        ) : (
                          <div
                            className={`text-sm font-medium italic text-gray-500 ${edit.isEditing ? 'cursor-pointer hover:text-emerald-600 transition-colors' : ''}`}
                            onClick={edit.isEditing ? () => edit.setRecordClassificationPickerState({ open: true, level: 1, level1: null, level2: null }) : undefined}
                          >
                            {edit.isEditing ? 'Click to assign classification' : 'No classification assigned'}
                          </div>
                        )}

                        {/* Retention Policy */}
                        {SHOW_RETENTION_AND_DISPOSAL && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                              <h4 className="text-sm font-bold text-gray-700 flex items-center">
                                Retention Policy
                                <Tooltip content="Determined by the Record Classification."><Info className="w-3.5 h-3.5 ml-1.5 text-gray-400" /></Tooltip>
                              </h4>
                              <div className="text-xs text-gray-500 mt-1">Trigger: <span className="font-medium text-gray-700">{rc?.retention_period_start_condition || 'N/A'}</span></div>
                            </div>
                            <div className="bg-gray-50 px-4 py-2 rounded-lg border border-gray-200 shadow-sm min-w-[260px]">
                              <div className="flex items-center">
                                <Clock className="w-4 h-4 text-emerald-600 mr-3 shrink-0" />
                                <span className="text-[10px] uppercase font-bold tracking-wider text-gray-500 mr-2">Min. Retention:</span>
                                <span className="text-[10px] uppercase font-bold tracking-wider text-gray-500">{formatRetentionPeriod(rc?.minimum_retention_period)}</span>
                              </div>
                              <div className="flex items-center mt-1 ml-7">
                                <span className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Action: {rc?.disposal_recommendation || 'Unknown'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Business Function */}
                {(() => {
                  const fc = edit.editedFields.function_classification ?? record.function_classification;
                  const hasFunction = fc?.domain?.domain_name || fc?.subdomain?.subdomain_name;
                  return (
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                      <div className="p-4 bg-gray-50/50 border-b border-gray-100 rounded-t-xl flex justify-between items-center">
                        <label className="text-sm font-bold text-gray-900 flex items-center">
                          <Database className="w-4 h-4 text-emerald-600 mr-2" />
                          Business Function
                          <Tooltip content="The primary business activity this document relates to."><Info className="w-3.5 h-3.5 text-gray-400 ml-1.5 cursor-help" /></Tooltip>
                        </label>
                      </div>
                      <div className="p-4 space-y-3">
                        {hasFunction ? (
                          <div className="space-y-3">
                            <div>
                              <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400 mb-0.5">Domain</div>
                              <div
                                className={`text-sm font-semibold text-gray-900 flex items-center ${edit.isEditing ? 'cursor-pointer hover:text-emerald-600 transition-colors' : ''}`}
                                onClick={edit.isEditing ? () => edit.setFunctionClassificationPickerState({ open: true, level: 1, domain: null, subdomain: null }) : undefined}
                              >
                                {edit.isEditing && <ChevronRight className="w-3 h-3 mr-1 text-emerald-600 shrink-0" />}
                                {fc?.domain?.domain_name || <span className="italic text-gray-400">Not set</span>}
                              </div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400 mb-0.5">Subdomain</div>
                              <div
                                className={`text-sm font-semibold text-emerald-800 flex items-center ${edit.isEditing ? 'cursor-pointer hover:text-emerald-600 transition-colors' : ''}`}
                                onClick={edit.isEditing && fc?.domain?.domain_key ? () => edit.setFunctionClassificationPickerState({ open: true, level: 2, domain: fc.domain.domain_key, subdomain: null }) : undefined}
                              >
                                {edit.isEditing && <ChevronRight className="w-3 h-3 mr-1 text-emerald-600 shrink-0" />}
                                {fc?.subdomain?.subdomain_name || <span className="italic text-gray-400">Not set</span>}
                              </div>
                            </div>
                            {fc?.function?.function_name && (
                              <div>
                                <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400 mb-0.5">Function</div>
                                <div
                                  className={`text-sm font-semibold text-emerald-700 flex items-center ${edit.isEditing ? 'cursor-pointer hover:text-emerald-600 transition-colors' : ''}`}
                                  onClick={edit.isEditing && fc?.domain?.domain_key && fc?.subdomain?.subdomain_key ? () => edit.setFunctionClassificationPickerState({ open: true, level: 3, domain: fc.domain.domain_key, subdomain: fc.subdomain.subdomain_key }) : undefined}
                                >
                                  {edit.isEditing && <ChevronRight className="w-3 h-3 mr-1 text-emerald-600 shrink-0" />}
                                  {fc.function.function_name}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div
                            className={`text-sm font-medium italic text-gray-500 ${edit.isEditing ? 'cursor-pointer hover:text-emerald-600 transition-colors' : ''}`}
                            onClick={edit.isEditing ? () => edit.setFunctionClassificationPickerState({ open: true, level: 1, domain: null, subdomain: null }) : undefined}
                          >
                            {edit.isEditing ? 'Click to assign classification' : 'No classification assigned'}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Governance & Sensitivity */}
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                  <div className="p-4 bg-gray-50/50 border-b border-gray-100 rounded-t-xl">
                    <label className="text-sm font-bold text-gray-900 flex items-center">
                      <ShieldAlert className="w-4 h-4 text-emerald-600 mr-2" />
                      Governance & Sensitivity
                    </label>
                  </div>

                  <div className="divide-y divide-gray-100">

                    {/* Information Security Classification */}
                    <div className={`p-5 ${getIscRowBackground(edit.getFieldValue('information_security_classification.level') ?? record.information_security_classification?.level)}`}>
                      <div className="flex justify-between items-start">
                        <h4 className="text-sm font-bold text-gray-900 flex items-center">
                          Information Security Classification
                          <Tooltip
                            interactive
                            position="bottom"
                            content={
                              <span className="whitespace-pre-line">
                                <span className="font-bold block mb-1">{formatIscLevelName(edit.getFieldValue('information_security_classification.level') ?? record.information_security_classification?.level)}</span>
                                {getIscDescription(edit.getFieldValue('information_security_classification.level') ?? record.information_security_classification?.level)}
                                <a
                                  href={PSR_CLASSIFICATION_URL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block mt-1.5 text-emerald-400 hover:text-emerald-300 underline"
                                >
                                  Learn more <ExternalLink className="w-3 h-3 inline ml-0.5" />
                                </a>
                              </span>
                            }
                          >
                            <Info className="w-3.5 h-3.5 text-gray-400 ml-1.5 cursor-help" />
                          </Tooltip>
                        </h4>
                        {edit.isEditing ? (
                          <div className="relative" ref={edit.iscDropdownRef}>
                            <button
                              type="button"
                              onClick={() => edit.setIscDropdownOpen(!edit.iscDropdownOpen)}
                              className="text-sm font-medium border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 flex items-center gap-2"
                            >
                              {ISC_OPTIONS.find(o => o.value === (edit.getFieldValue('information_security_classification.level') ?? record.information_security_classification?.level ?? 'unclassified'))?.label ?? 'Unclassified'}
                              <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${edit.iscDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {edit.iscDropdownOpen && (
                              <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 flex flex-col">
                                {ISC_OPTIONS.map(opt => (
                                  <Tooltip
                                    key={opt.value}
                                    position="left"
                                    content={<span className="whitespace-pre-line">{getIscDescription(opt.value)}</span>}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => {
                                        edit.setEditField('information_security_classification.level', opt.value);
                                        edit.setIscDropdownOpen(false);
                                      }}
                                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                        (edit.getFieldValue('information_security_classification.level') ?? record.information_security_classification?.level ?? 'unclassified') === opt.value
                                          ? 'font-bold text-emerald-700 bg-emerald-50'
                                          : 'text-gray-700'
                                      }`}
                                    >
                                      {opt.label}
                                    </button>
                                  </Tooltip>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <IscBadge level={record.information_security_classification?.level} />
                        )}
                      </div>
                    </div>

                    {/* Personal Information */}
                    {(() => {
                      const pii = edit.getFieldValue('personal_information') ?? record.personal_information;
                      const piiDetected = pii?.detected ?? false;
                      const rawLevel = pii?.sensitivity_level ?? '';
                      const piiLevel = ['standard', 'sensitive'].includes(String(rawLevel).toLowerCase())
                        ? String(rawLevel).toLowerCase()
                        : 'standard';
                      return (
                    <div className={`p-5 ${piiDetected ? 'bg-red-50/50' : 'bg-white'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="text-sm font-bold text-gray-900 flex items-center">
                          Personal Information
                          <Tooltip content="Names, contact info, and sensitive identifiers."><Info className="w-3.5 h-3.5 text-gray-400 ml-1.5 cursor-help" /></Tooltip>
                        </h4>
                        {edit.isEditing ? (
                          <ToggleSwitch
                            enabled={piiDetected}
                            onChange={(val) => {
                              const currentLevel = pii?.sensitivity_level;
                              const normalised = currentLevel?.toLowerCase();
                              edit.setEditField('personal_information', {
                                detected: val,
                                sensitivity_level: val
                                  ? (!normalised || normalised === 'none' ? 'standard' : currentLevel)
                                  : null,
                              });
                            }}
                          />
                        ) : (
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${record.personal_information?.detected ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                            {record.personal_information?.detected ? 'Detected' : 'Not Detected'}
                          </span>
                        )}
                      </div>

                      {edit.isEditing && piiDetected && (
                        <div className="flex items-center gap-2 mt-2 mb-1">
                          <span className="text-xs font-semibold text-gray-600">Sensitivity</span>
                          <select
                            className="text-sm font-bold text-gray-900 bg-gray-50 border border-gray-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 rounded-lg px-2 py-1 transition-colors"
                            value={piiLevel}
                            onChange={(e) => edit.setEditField('personal_information', {
                              detected: true,
                              sensitivity_level: e.target.value,
                            })}
                          >
                            {PII_SENSITIVITY_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value} title={opt.description}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {record.personal_information?.entity_recognition && record.personal_information.entity_recognition.length > 0 && (
                        <div className="mt-3">
                          <button
                            onClick={() => setPiiEntitiesExpanded(!piiEntitiesExpanded)}
                            className="font-semibold text-red-700 text-xs flex items-center hover:text-red-800"
                          >
                            <ChevronRight className={`w-3 h-3 mr-1 transition-transform ${piiEntitiesExpanded ? 'rotate-90' : ''}`} />
                            View Extracted Entities ({record.personal_information.entity_recognition.length})
                          </button>
                          {piiEntitiesExpanded && (
                            <div className="mt-2 space-y-2 pl-4">
                              {record.personal_information.entity_recognition.map((ent, i) => (
                                <div key={i} className="flex flex-col bg-red-50/50 p-2 rounded">
                                  <div className="flex justify-between items-center mb-1">
                                    <div className="flex items-center">
                                      <span className="font-bold text-sm">{ent.entity}</span>
                                      <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 uppercase tracking-wider">{ent.sensitivity_level}</span>
                                    </div>
                                    <span className="bg-red-200 px-1.5 py-0.5 rounded text-sm text-red-800">{ent.category}</span>
                                  </div>
                                  <span className="italic text-gray-500 text-xs">&ldquo;...{ent.context_snippet}...&rdquo;</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                      );
                    })()}

                    {/* Maori Data */}
                    <div className={`p-5 ${(edit.getFieldValue('maori_data_sovereignty.detected') ?? record.maori_data_sovereignty?.detected) ? 'bg-purple-50/50' : 'bg-white'}`}>
                      <div className="flex justify-between items-start">
                        <h4 className="text-sm font-bold text-gray-900 flex items-center">
                          Māori Data
                          <Tooltip content="Content related to Māori data, culture, or people."><Info className="w-3.5 h-3.5 text-gray-400 ml-1.5 cursor-help" /></Tooltip>
                          {record.maori_data_sovereignty?.detected && record.maori_data_sovereignty?.category && (
                            <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700 uppercase tracking-wider">
                              {record.maori_data_sovereignty.category}
                            </span>
                          )}
                        </h4>
                        {edit.isEditing ? (
                          <ToggleSwitch
                            enabled={edit.getFieldValue('maori_data_sovereignty.detected') ?? record.maori_data_sovereignty?.detected ?? false}
                            onChange={(val) => edit.setEditField('maori_data_sovereignty.detected', val)}
                          />
                        ) : (
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${record.maori_data_sovereignty?.detected ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'}`}>
                            {record.maori_data_sovereignty?.detected ? 'Detected' : 'Not Detected'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Redacted copies released from this record */}
                {redactionsHalHref && (
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                    <div
                      className="p-4 bg-gray-50/50 border-b border-gray-100 flex justify-between items-center rounded-t-xl cursor-pointer"
                      onClick={() => setRedactionsExpanded(!redactionsExpanded)}
                    >
                      <label className="text-sm font-bold text-gray-900 flex items-center cursor-pointer">
                        <HatGlasses className="w-4 h-4 text-emerald-600 mr-2" />
                        Redacted Copies
                        {redactionsLoading
                          ? <Loader2 className="w-3 h-3 ml-2 text-gray-400 animate-spin" />
                          : <span className="ml-2 text-xs text-gray-500 font-normal">({redactions.length} cop{redactions.length !== 1 ? 'ies' : 'y'})</span>
                        }
                      </label>
                      {redactionsExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>

                    {redactionsExpanded && (
                      <div className="p-4">
                        <RedactionsList
                          redactions={redactions}
                          isLoading={redactionsLoading}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Version History */}
                {versionHistoryHalHref && (
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                    <div
                      className="p-4 bg-gray-50/50 border-b border-gray-100 flex justify-between items-center rounded-t-xl cursor-pointer"
                      onClick={() => setVersionExpanded(!versionExpanded)}
                    >
                      <label className="text-sm font-bold text-gray-900 flex items-center cursor-pointer">
                        <History className="w-4 h-4 text-emerald-600 mr-2" />
                        Version History
                        {versionHistoryLoading
                          ? <Loader2 className="w-3 h-3 ml-2 text-gray-400 animate-spin" />
                          : <span className="ml-2 text-xs text-gray-500 font-normal">({versionHistory.length} version{versionHistory.length !== 1 ? 's' : ''})</span>
                        }
                      </label>
                      {versionExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>

                    {versionExpanded && (
                      <div className="p-4 space-y-2">
                        {versionHistoryLoading ? (
                          <div className="flex items-center justify-center py-6">
                            <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
                          </div>
                        ) : versionHistory.length === 0 ? (
                          <p className="text-sm text-gray-400 italic">No version history available.</p>
                        ) : (
                          versionHistory.map((v) => {
                            const isCurrentVersionRow =
                              v.version != null && v.version === latestVersionInHistory;
                            const isViewing = versionId
                              ? v.id === versionId
                              : isCurrentVersionRow;
                            return (
                              <div
                                key={v.id}
                                className={`flex items-center justify-between p-3 rounded-xl border transition-all ${isViewing ? 'bg-emerald-50 border-emerald-200 shadow-sm' : 'bg-white border-gray-100'}`}
                              >
                                <div className="flex items-center space-x-3">
                                  <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-sm font-bold ${isViewing ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                                    v{v.version || '?'}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {v.file?.name || record.file.name}
                                      {!isCurrentVersionRow && <span className="ml-2 text-xs text-amber-600 font-medium">(Previous version)</span>}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      {formatDateTime(v.metadata?.modified || v.metadata?.created)} · {formatStatusLabel(v.status)}
                                    </p>
                                  </div>
                                </div>
                                {!isViewing && id && (
                                  <button
                                    type="button"
                                    onClick={() => window.open(`/records/${id}/versions/${v.id}`, '_blank', 'noopener,noreferrer')}
                                    className="text-xs font-medium text-gray-500 hover:text-emerald-600 px-3 py-1.5 rounded-lg border border-gray-200 hover:border-emerald-200 transition-colors flex items-center"
                                  >
                                    <ExternalLink className="w-3 h-3 mr-1" />
                                    Open
                                  </button>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Groups */}
                {groupsHalHref && groups.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                    <div
                      className="p-4 bg-gray-50/50 border-b border-gray-100 flex justify-between items-center rounded-t-xl cursor-pointer"
                      onClick={() => setGroupExpanded(!groupExpanded)}
                    >
                      <label className="text-sm font-bold text-gray-900 flex items-center cursor-pointer">
                        <Layers className="w-4 h-4 text-emerald-600 mr-2" />
                        Groups
                        <span className="ml-2 text-xs text-gray-500 font-normal">({groups.length} group{groups.length !== 1 ? 's' : ''})</span>
                      </label>
                      {groupExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>

                    {groupExpanded && (
                      <div className="p-4 space-y-4">
                        {groups.map((group) => (
                          <div key={group.id}>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                              <button
                                type="button"
                                onClick={() => navigate(`/groups/${group.id}`)}
                                className="text-emerald-700 hover:text-emerald-800 hover:underline transition-colors"
                              >
                                {formatGroupLabel(group)}
                              </button>
                              <span className="ml-2 font-normal normal-case tracking-normal">({group.records.length} record{group.records.length !== 1 ? 's' : ''})</span>
                            </p>
                            <div className="space-y-2">
                              {group.records.map((member) => {
                                const isCurrent = member.id === record.id;
                                return (
                                  <div
                                    key={member.id}
                                    onClick={isCurrent ? undefined : () => navigate(`/records/${member.id}`)}
                                    className={`flex items-center p-3 rounded-xl border transition-all ${isCurrent
                                      ? 'bg-emerald-50 border-emerald-200 shadow-sm'
                                      : 'bg-white border-gray-100 cursor-pointer hover:border-emerald-200 hover:shadow-sm'}`}
                                  >
                                    <div className="w-8 h-8 shrink-0 flex items-center justify-center">
                                      {getFileIcon(member.content_type || '', 'w-6 h-6')}
                                    </div>
                                    <p className="text-sm font-medium text-gray-900 ml-3 truncate">
                                      {member.title || 'Untitled record'}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Ingestion Details */}
                {ingestionProcessHalHref && (
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                    <div
                      className="p-4 bg-gray-50/50 border-b border-gray-100 flex justify-between items-center rounded-t-xl cursor-pointer"
                      onClick={() => setIngestionExpanded(!ingestionExpanded)}
                    >
                      <label className="text-sm font-bold text-gray-900 flex items-center cursor-pointer">
                        <Settings className="w-4 h-4 text-emerald-600 mr-2" />
                        Ingestion Details
                        {ingestionLoading && <Loader2 className="w-3 h-3 ml-2 text-gray-400 animate-spin" />}
                      </label>
                      {ingestionExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>

                    {ingestionExpanded && (
                      ingestionLoading ? (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
                        </div>
                      ) : ingestionDetails ? (
                        <div className="p-4 space-y-1 divide-y divide-gray-50">
                          <MetadataRow label="Process ID" value={ingestionDetails.id} />
                          <MetadataRow label="AI Processing Mode" value={ingestionDetails.ai_processing_mode} />
                          <MetadataRow label="AI Previous Version Mode" value={ingestionDetails.ai_processing_previous_version_mode} />
                          <MetadataRow label="Auto Process" value={ingestionDetails.auto_process ? 'Yes' : 'No'} />
                          <MetadataRow label="Notify on Completion" value={ingestionDetails.notify_user_on_completion ? 'Yes' : 'No'} />
                          <MetadataRow label="Initiated At" value={formatDateTime(ingestionDetails.initiated_at)} icon={<Calendar className="w-3.5 h-3.5" />} />
                          <MetadataRow label="Initiated By" value={ingestionDetails.initiated_by} icon={<User className="w-3.5 h-3.5" />} />
                          <MetadataRow label="Uploaded At" value={formatDateTime(ingestionDetails.uploaded_at)} icon={<Calendar className="w-3.5 h-3.5" />} />
                          <MetadataRow label="Completed At" value={formatDateTime(ingestionDetails.completed_at)} icon={<Calendar className="w-3.5 h-3.5" />} />
                          <MetadataRow label="Confirmed At" value={formatDateTime(ingestionDetails.confirmed_at)} icon={<Calendar className="w-3.5 h-3.5" />} />
                          <MetadataRow label="Confirmed By" value={ingestionDetails.confirmed_by} icon={<User className="w-3.5 h-3.5" />} />

                          {ingestionDetails.custom_headers && Object.keys(ingestionDetails.custom_headers).length > 0 && (
                            <div className="pt-3">
                              <details className="group/headers">
                                <summary className="text-xs text-emerald-600 font-semibold cursor-pointer select-none hover:text-emerald-700 list-none flex items-center">
                                  <ChevronRight className="w-3 h-3 mr-1 transition-transform group-open/headers:rotate-90" />
                                  Custom Headers ({Object.keys(ingestionDetails.custom_headers).length})
                                </summary>
                                <div className="mt-2 text-xs bg-gray-50 p-3 rounded border border-gray-100 space-y-1.5">
                                  {Object.entries(ingestionDetails.custom_headers).map(([key, value]) => (
                                    <div key={key} className="flex justify-between">
                                      <span className="text-gray-500 font-mono">{key}</span>
                                      <span className="text-gray-900 font-medium ml-4 text-right truncate max-w-[60%]" title={String(value)}>{String(value)}</span>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400 italic p-4">No ingestion details available.</p>
                      )
                    )}
                  </div>
                )}

                {/* Audit Logs */}
                {auditLogsHalHref && (
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                    <div
                      className="p-4 bg-gray-50/50 border-b border-gray-100 flex justify-between items-center rounded-t-xl cursor-pointer"
                      onClick={() => setAuditExpanded(!auditExpanded)}
                    >
                      <label className="text-sm font-bold text-gray-900 flex items-center cursor-pointer">
                        <ScrollText className="w-4 h-4 text-emerald-600 mr-2" />
                        Audit Logs
                        {auditLogsLoading
                          ? <Loader2 className="w-3 h-3 ml-2 text-gray-400 animate-spin" />
                          : <span className="ml-2 text-xs text-gray-500 font-normal">({auditLogs.length})</span>
                        }
                      </label>
                      {auditExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>

                    {auditExpanded && (
                      <div className="p-4">
                        {auditLogsLoading ? (
                          <div className="flex items-center justify-center py-6">
                            <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
                          </div>
                        ) : auditLogs.length === 0 ? (
                          <p className="text-sm text-gray-400 italic">No audit logs available.</p>
                        ) : (
                          <div className="space-y-2">
                            {auditLogs.map((log) => (
                              <div key={log.id} className="flex items-start justify-between p-3 rounded-lg bg-gray-50 border border-gray-100">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-medium text-gray-900">{formatAuditAction(log.action)}</span>
                                    <span className="text-xs text-gray-400">by {log.user_id || 'system'}</span>
                                  </div>
                                  {log.details && Object.keys(log.details).length > 0 && (
                                    <div className="text-xs text-gray-500">
                                      {Object.entries(log.details).map(([k, v]) => (
                                        <span key={k} className="mr-3">{k}: <span className="font-medium">{String(v)}</span></span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <span className="text-xs text-gray-400 shrink-0 ml-4">{formatDateTime(log.timestamp)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>

            {/* Actions Bar (bottom of card, visible when editing) */}
            {edit.isEditing && (
              <div className="bg-gray-50 border-t border-gray-200 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 rounded-b-3xl">
                <div className="flex-1 md:mr-6">
                  <div className="flex items-center text-sm text-gray-600 bg-blue-50/50 text-blue-800 px-4 py-3 rounded-xl border border-blue-100">
                    <Info className="w-5 h-5 mr-3 shrink-0 text-blue-500" />
                    <p>Review all metadata carefully. Changes are logged for audit purposes.</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 shrink-0 justify-end w-full md:w-auto">
                  <button
                    onClick={edit.handleCancelEdit}
                    disabled={edit.isSaving}
                    className="px-6 py-2.5 rounded-xl text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Discard Changes
                  </button>
                  <button
                    onClick={edit.handleSave}
                    disabled={edit.isSaving || Object.keys(edit.editedFields).length === 0 || edit.addressIsStale}
                    className={`px-8 py-2.5 rounded-xl text-sm font-bold text-white shadow-lg shadow-emerald-200 flex items-center transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none ${BRAND_BG}`}
                  >
                    {edit.isSaving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {edit.isSaving ? 'Saving...' : 'Save Record'}
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </main>

      <footer className="bg-white border-t border-gray-200 mt-auto py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-gray-500">
          <p>&copy; 2026 Palmerston North City Council &bull; ARA</p>
        </div>
      </footer>

      {/* Classification Picker Modals */}
      {edit.recordClassificationPickerState.open && (
        <RecordClassificationPicker
          initialLevel={edit.recordClassificationPickerState.level}
          initialLevel1={edit.recordClassificationPickerState.level1}
          initialLevel2={edit.recordClassificationPickerState.level2}
          onSelect={(entry) => {
            edit.setEditField('record_classification_id', entry.classification_id);
            edit.setEditField('record_classification', {
              level_1: entry.level_1,
              level_2: entry.level_2,
              level_3: entry.level_3,
              disposal_recommendation: entry.disposal_recommendation,
              minimum_retention_period: entry.minimum_retention_period,
              retention_period_start_condition: entry.retention_period_start_condition,
            });
            edit.setRecordClassificationPickerState(s => ({ ...s, open: false }));
          }}
          onClose={() => edit.setRecordClassificationPickerState(s => ({ ...s, open: false }))}
        />
      )}
      {edit.functionClassificationPickerState.open && (
        <FunctionClassificationPicker
          initialLevel={edit.functionClassificationPickerState.level}
          initialDomain={edit.functionClassificationPickerState.domain}
          initialSubdomain={edit.functionClassificationPickerState.subdomain}
          onSelect={(selection) => {
            edit.setEditField('function_classification_id', selection.classification_id);
            edit.setEditField('function_classification', {
              domain: selection.domain,
              subdomain: selection.subdomain,
              function: selection.function,
            });
            edit.setFunctionClassificationPickerState(s => ({ ...s, open: false }));
          }}
          onClose={() => edit.setFunctionClassificationPickerState(s => ({ ...s, open: false }))}
        />
      )}

      {/* Redact and download */}
      {canRedactRecord && (
        <RedactionModal
          isOpen={redactionModalOpen}
          recordId={id!}
          categoriesHref={redactionCategoriesHref!}
          candidatesHref={redactionCandidatesHalHref!}
          createHref={createRedactionHalHref!}
          onClose={() => setRedactionModalOpen(false)}
          onCreated={(created) => {
            setRedactions((current) => [created, ...current]);
            setRedactionsExpanded(true);
          }}
        />
      )}

      {/* Finalise confirmation */}
      <ConfirmPopUp
        isOpen={finaliseConfirmOpen}
        title="Mark record as final?"
        message="This record will become final. Once complete, no edits can be made to the record or the metadata."
        confirmLabel="Mark as Final"
        isProcessing={edit.isFinalising}
        onConfirm={async () => {
          await edit.finalise();
          setFinaliseConfirmOpen(false);
        }}
        onCancel={() => setFinaliseConfirmOpen(false)}
      />

      {/* Unfinalise confirmation */}
      <ConfirmPopUp
        isOpen={unfinaliseConfirmOpen}
        title="Unfinalise record?"
        message="This record will return to draft and become editable again."
        confirmLabel="Unfinalise"
        isProcessing={edit.isUnfinalising}
        onConfirm={async () => {
          await edit.unfinalise();
          setUnfinaliseConfirmOpen(false);
        }}
        onCancel={() => setUnfinaliseConfirmOpen(false)}
      />

      {/* Delete confirmation */}
      <ConfirmPopUp
        isOpen={deleteConfirmOpen}
        title="Delete this record?"
        message="The record will no longer be available for viewing or editing, it can be restored for a limited time if needed."
        confirmLabel="Delete Record"
        isProcessing={edit.isDeleting}
        onConfirm={async () => {
          if (await edit.deleteRecord()) {
            setDeleteConfirmOpen(false);
            setDeleteSuccessVisible(true);
          }
        }}
        onCancel={() => setDeleteConfirmOpen(false)}
      />

      {/* Delete success */}
      <ConfirmPopUp
        isOpen={deleteSuccessVisible}
        title="Record deleted"
        message="The record has been deleted."
        tone="success"
        confirmLabel="Okay"
        hideCancel
        onConfirm={() => navigate(-1)}
        onCancel={() => navigate(-1)}
      />

      {/* Restore confirmation */}
      <ConfirmPopUp
        isOpen={restoreConfirmOpen}
        title="Restore this record?"
        message="The record will be restored to its previous status."
        confirmLabel="Restore Record"
        isProcessing={edit.isRestoring}
        onConfirm={async () => {
          if (await edit.restoreRecord()) {
            setRestoreConfirmOpen(false);
            reloadRecord();
          }
        }}
        onCancel={() => setRestoreConfirmOpen(false)}
      />

      {newVersionModalOpen && newVersionHalHref && id && (
        <UploadNewVersionModal
          href={newVersionHalHref}
          recordId={id}
          recordTitle={record.title}
          onClose={() => setNewVersionModalOpen(false)}
        />
      )}

      {addToGroupModalOpen && id && (
        <AddToGroupModal
          recordIds={[id]}
          existingGroupIds={groups.map((g) => g.id)}
          onClose={() => setAddToGroupModalOpen(false)}
          onAdded={(group, counts) => {
            setAddToGroupModalOpen(false);
            edit.showToast(addedToGroupMessage(counts), counts.addedCount === 0 ? 'info' : 'success', {
              href: `/groups/${group.id}`,
              label: formatGroupLabel(group),
            });
            if (counts.addedCount > 0) reloadRecord();
          }}
        />
      )}

      {/* Toast */}
      <div className="fixed bottom-6 right-6 z-50">
        <Toast
          message={edit.toastMessage}
          type={edit.toastType}
          isVisible={edit.toastVisible}
          link={edit.toastLink}
          onClose={edit.hideToast}
        />
      </div>
    </div>
  );
}

// --- Shared Navbar ---

function Navbar({ navigate }: { navigate: ReturnType<typeof useNavigate> }) {
  return (
    <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          <div className="flex items-center space-x-3 group cursor-pointer" onClick={() => navigate('/')}>
            <div className={`w-10 h-10 ${BRAND_BG} rounded-xl shadow-lg shadow-emerald-200 flex items-center justify-center text-white transform group-hover:scale-110 transition-transform duration-300`}>
              <Database className="w-5 h-5" />
            </div>
            <div>
              <span className={`text-xl font-bold tracking-tight ${BRAND_TEXT}`}>ARA</span>
              <span className="text-xs text-gray-400 block font-medium -mt-1 tracking-widest opacity-60">Automated Records Assistant</span>
            </div>
          </div>
          <AppNavActions />
        </div>
      </div>
    </nav>
  );
}
